import { UseFormReturnType } from "@mantine/form"
import {
  CountrySelector,
  LabelWithTooltip,
  MultiSelect,
  NumberInput,
  Select,
  Switch,
  TextInput,
} from "@tfg/ui"

import { SelectListRegistrationIdField } from "~/components/SelectListRegistrationIdField"
import { COUNTRIES } from "~/utils/countries"

import type { SchemaFieldRenderer } from "./schema-form.types"

interface BaseProps {
  readonly name: string
  readonly label: string
  readonly tooltip: string | undefined
  readonly isRequired: boolean
  readonly placeholder: string | undefined
  readonly disabled: boolean | undefined
  readonly error: string | undefined
  readonly form: UseFormReturnType<Record<string, unknown>>
}

interface TextFieldRendererProps extends BaseProps {}

const TextFieldRenderer: SchemaFieldRenderer = ({
  name,
  label,
  tooltip,
  placeholder,
  disabled,
  isRequired,
  error,
  form,
}: TextFieldRendererProps) => {
  const inputProps = form.getInputProps(name)
  return (
    <TextInput
      label={
        <LabelWithTooltip
          label={label}
          hoverHelperText={tooltip}
          withAsterisk={isRequired}
          w="100%"
          justify="space-between"
        />
      }
      labelProps={{
        w: "100%",
      }}
      w="100%"
      placeholder={placeholder}
      disabled={disabled}
      {...inputProps}
      error={error ?? inputProps.error}
      onChange={(event) => {
        form.setFieldValue(name, event.currentTarget.value)
        form.validateField(name)
      }}
    />
  )
}

interface NumberFieldRendererProps extends BaseProps {}

const NumberFieldRenderer: SchemaFieldRenderer = ({
  name,
  label,
  tooltip,
  isRequired,
  placeholder,
  disabled,
  error,
  form,
}: NumberFieldRendererProps) => {
  const inputProps = form.getInputProps(name)
  return (
    <NumberInput
      label={
        <LabelWithTooltip
          label={label}
          hoverHelperText={tooltip}
          withAsterisk={isRequired}
          w="100%"
        />
      }
      labelProps={{
        w: "100%",
      }}
      placeholder={placeholder}
      disabled={disabled}
      {...inputProps}
      error={error ?? inputProps.error}
      onChange={(value) => {
        form.setFieldValue(name, value)
        // need to validate the whole form because
        // latitude and longitude are valdiated together in super rafine
        form.validate()
      }}
    />
  )
}

interface BooleanFieldRendererProps extends BaseProps {}

const BooleanFieldRenderer: SchemaFieldRenderer = ({
  name,
  label,
  isRequired,
  tooltip,
  disabled,
  error,
  form,
}: BooleanFieldRendererProps) => {
  const inputProps = form.getInputProps(name, { type: "checkbox" })
  return (
    <Switch
      label={
        <LabelWithTooltip
          label={label}
          hoverHelperText={tooltip}
          withAsterisk={isRequired}
          w="100%"
        />
      }
      disabled={disabled}
      {...inputProps}
      error={error ?? inputProps.error}
    />
  )
}

interface SelectFieldRendererProps extends BaseProps {
  readonly options:
    | ReadonlyArray<{
        readonly label: string
        readonly value: string
      }>
    | undefined
}

const SelectFieldRenderer: SchemaFieldRenderer = ({
  name,
  label,
  isRequired,
  tooltip,
  placeholder,
  disabled,
  options,
  error,
  form,
}: SelectFieldRendererProps) => {
  const inputProps = form.getInputProps(name)
  return (
    <Select
      label={
        <LabelWithTooltip
          label={label}
          hoverHelperText={tooltip}
          withAsterisk={isRequired}
        />
      }
      labelProps={{
        w: "100%",
      }}
      placeholder={placeholder}
      disabled={disabled}
      searchable
      clearable
      data={
        options?.map((opt) => ({ label: opt.label, value: opt.value })) ?? []
      }
      {...inputProps}
      error={error ?? inputProps.error}
      onChange={(value) => {
        form.setFieldValue(name, value)
        form.validateField(name)
      }}
    />
  )
}

interface MultiSelectFieldRendererProps extends BaseProps {
  readonly options:
    | ReadonlyArray<{
        readonly label: string
        readonly value: string
      }>
    | undefined
}

const MultiSelectFieldRenderer: SchemaFieldRenderer = ({
  name,
  label,
  isRequired,
  tooltip,
  placeholder,
  disabled,
  options,
  error,
  form,
}: MultiSelectFieldRendererProps) => {
  const inputProps = form.getInputProps(name)
  const value = (inputProps.value as string[] | undefined) ?? []
  return (
    <MultiSelect
      label={
        <LabelWithTooltip
          label={label}
          hoverHelperText={tooltip}
          withAsterisk={isRequired}
          w="100%"
        />
      }
      placeholder={placeholder}
      disabled={disabled}
      searchable
      size="xs"
      clearable
      labelProps={{
        w: "100%",
      }}
      data={
        options?.map((opt) => ({ label: opt.label, value: opt.value })) ?? []
      }
      value={value}
      error={error ?? inputProps.error}
      onChange={(values) => {
        form.setFieldValue(name, values)
        form.validateField(name)
      }}
    />
  )
}

interface SelectListRegistrationIdRendererProps extends BaseProps {
  readonly options:
    | ReadonlyArray<{
        readonly label: string
        readonly value: string
        readonly description?: string
      }>
    | undefined
}

const SelectListRegistrationIdRenderer: SchemaFieldRenderer = ({
  name,
  label,
  tooltip,
  isRequired,
  placeholder,
  disabled,
  options,
  error: fieldError,
  form,
}: SelectListRegistrationIdRendererProps) => {
  const value =
    (form.values[name] as
      | { id?: string; registrationId: string; type: string }[]
      | undefined) ?? []

  return (
    <SelectListRegistrationIdField
      value={value}
      onChange={(list) => {
        form.setFieldValue(name, list)
        form.validateField(name)
      }}
      onValidate={() => form.validateField(name)}
      options={options?.map((o) => ({ ...o })) ?? []}
      label={label}
      tooltip={tooltip}
      isRequired={isRequired}
      placeholder={placeholder}
      disabled={disabled}
      fieldError={fieldError}
    />
  )
}

interface CountrySelectFieldRendererProps extends BaseProps {}

const CountrySelectFieldRenderer: SchemaFieldRenderer = ({
  name,
  label,
  tooltip,
  isRequired,
  disabled,
  placeholder,
  error,
  form,
}: CountrySelectFieldRendererProps) => {
  const inputProps = form.getInputProps(name)
  return (
    <CountrySelector
      label={
        <LabelWithTooltip
          label={label}
          hoverHelperText={tooltip}
          withAsterisk={isRequired}
        />
      }
      labelProps={{
        w: "100%",
      }}
      searchable
      placeholder={placeholder}
      disabled={disabled}
      countriesMapper={COUNTRIES}
      {...inputProps}
      value={inputProps.value ?? null}
      error={error ?? inputProps.error}
      onChange={(value) => {
        form.setFieldValue(name, value)
        form.validateField(name)
      }}
    />
  )
}

export const DEFAULT_FIELD_RENDERERS: Record<string, SchemaFieldRenderer> = {
  text: TextFieldRenderer,
  number: NumberFieldRenderer,
  boolean: BooleanFieldRenderer,
  select: SelectFieldRenderer,
  multiSelect: MultiSelectFieldRenderer,
  selectListRegistrationId: SelectListRegistrationIdRenderer,
  countrySelect: CountrySelectFieldRenderer,
}
